import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { PlusCircle, AlertCircle } from "lucide-react";

export default function PortalRequest() {
    const navigate = useNavigate();
    const [serviceTypes, setServiceTypes] = useState([]);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState({
        patient_name: "",
        patient_age: "",
        patient_sex: "M",
        patient_phone: "",
        origin_address: "",
        destination_address: "",
        service_type_code: "TBL",
        urgency_level: "normal",
        reason: "",
        needs_oxygen: false,
        needs_doctor: false,
        observations: "",
    });

    useEffect(() => {
        (async () => {
            try {
                const r = await api.get("/portal/service-types");
                if (r?.data) setServiceTypes(r.data);
            } catch (e) { console.warn(e); }
        })();
    }, []);

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const payload = { ...form };
            if (payload.patient_age) payload.patient_age = parseInt(payload.patient_age);
            else delete payload.patient_age;
            const { data } = await api.post("/portal/services", payload);
            toast.success(`Solicitud creada: ${data.folio}. Cabina la recibió.`);
            navigate(`/portal/services/${data.id}`);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || e.message);
        } finally {
            setSaving(false);
        }
    };

    const selected = serviceTypes.find((s) => s.code === form.service_type_code);

    return (
        <div className="max-w-4xl" data-testid="portal-request-page">
            <div className="fm-card p-5 mb-4 bg-slate-900 text-white flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                <div>
                    <div className="font-display font-bold text-base">Solicitud asíncrona — Tiempo de respuesta SLA según convenio</div>
                    <div className="text-xs text-slate-300 mt-1">
                        Para urgencias críticas inmediatas, llama también a nuestra línea 24/7: <span className="font-mono font-bold text-red-300">+52 55 2567 1884</span>
                    </div>
                </div>
            </div>

            <form onSubmit={submit} className="space-y-4">
                <div className="fm-card p-5">
                    <h3 className="font-display font-bold text-base mb-4 pb-3 border-b border-slate-200">Datos del paciente</h3>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                        <div className="md:col-span-2">
                            <label className="fm-label">Nombre del paciente *</label>
                            <input required value={form.patient_name} onChange={(e) => set("patient_name", e.target.value)} className="fm-input" data-testid="portal-patient-name" />
                        </div>
                        <div>
                            <label className="fm-label">Edad</label>
                            <input type="number" value={form.patient_age} onChange={(e) => set("patient_age", e.target.value)} className="fm-input" />
                        </div>
                        <div>
                            <label className="fm-label">Sexo</label>
                            <select value={form.patient_sex} onChange={(e) => set("patient_sex", e.target.value)} className="fm-input">
                                <option value="M">Masculino</option>
                                <option value="F">Femenino</option>
                                <option value="O">Otro</option>
                            </select>
                        </div>
                        <div className="md:col-span-2">
                            <label className="fm-label">Teléfono de contacto</label>
                            <input value={form.patient_phone} onChange={(e) => set("patient_phone", e.target.value)} className="fm-input" />
                        </div>
                    </div>
                </div>

                <div className="fm-card p-5">
                    <h3 className="font-display font-bold text-base mb-4 pb-3 border-b border-slate-200">Servicio requerido</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                            <label className="fm-label">Tipo de servicio *</label>
                            <select required value={form.service_type_code} onChange={(e) => set("service_type_code", e.target.value)} className="fm-input" data-testid="portal-service-type">
                                {serviceTypes.map((t) => <option key={t.code} value={t.code}>{t.name}</option>)}
                            </select>
                            {selected && (
                                <div className="text-[11px] text-slate-500 mt-1">Tarifa base: <span className="font-mono font-bold">${selected.base_price.toLocaleString()}</span> MXN + IVA. Se aplicará tu tarifa de convenio si corresponde.</div>
                            )}
                        </div>
                        <div>
                            <label className="fm-label">Nivel de urgencia</label>
                            <select value={form.urgency_level} onChange={(e) => set("urgency_level", e.target.value)} className="fm-input">
                                <option value="baja">Baja</option>
                                <option value="normal">Normal</option>
                                <option value="alta">Alta</option>
                                <option value="critica">Crítica</option>
                            </select>
                        </div>
                        <div className="md:col-span-2">
                            <label className="fm-label">Motivo / sintomatología</label>
                            <input value={form.reason} onChange={(e) => set("reason", e.target.value)} className="fm-input" placeholder="Ej. Dolor torácico, traslado programado..." />
                        </div>
                        <div>
                            <label className="fm-label">Dirección de origen *</label>
                            <input required value={form.origin_address} onChange={(e) => set("origin_address", e.target.value)} className="fm-input" data-testid="portal-origin" />
                        </div>
                        <div>
                            <label className="fm-label">Destino</label>
                            <input value={form.destination_address} onChange={(e) => set("destination_address", e.target.value)} className="fm-input" placeholder="Hospital, domicilio..." />
                        </div>
                    </div>
                    <div className="mt-3 pt-3 border-t border-slate-200 flex gap-4">
                        <label className="flex items-center gap-2 text-sm cursor-pointer">
                            <input type="checkbox" checked={form.needs_oxygen} onChange={(e) => set("needs_oxygen", e.target.checked)} />
                            Requiere oxígeno
                        </label>
                        <label className="flex items-center gap-2 text-sm cursor-pointer">
                            <input type="checkbox" checked={form.needs_doctor} onChange={(e) => set("needs_doctor", e.target.checked)} />
                            Requiere médico a bordo
                        </label>
                    </div>
                </div>

                <div className="fm-card p-5">
                    <label className="fm-label">Observaciones / instrucciones especiales</label>
                    <textarea value={form.observations} onChange={(e) => set("observations", e.target.value)} className="fm-input" rows={3} placeholder="Ej. Accesar por puerta lateral, paciente en piso 4, contacto al llegar..." />
                </div>

                <div className="flex items-center justify-end gap-3 sticky bottom-0 bg-slate-100 py-3">
                    <button type="submit" disabled={saving} className="fm-btn-accent" data-testid="portal-submit-btn">
                        <PlusCircle className="w-4 h-4" />
                        {saving ? "Enviando solicitud..." : "Enviar solicitud a Cabina"}
                    </button>
                </div>
            </form>
        </div>
    );
}
